import { EMPTY, from, fromEvent, merge, of, ReplaySubject, Subject } from 'rxjs';
import { bufferTime, map, mergeMap, startWith, switchMap, share, tap } from 'rxjs/operators';

import { authorizedFetch, readJson, postJson } from './http.js';

let defaultUserSettings = {
    customSystemPrompt: '',
    help: true,
    hereAndNow: true,
    geoLocation: false,
    internetResearch: true,
    collectKnowledge: true,
    collectMail: false,
    showNotifications: false,
    zeroTemperature: false,
    debug: false,
};

export let userSettings$ = of(undefined).pipe(
    switchMap(() => from(authorizedFetch('/api/user-settings'))),
    readJson(),
    map(server => mergeUserSettings(defaultUserSettings, server)),
    // update the locally stored settings in case they have been
    // modified on another device previously.
    tap(settings => saveUserSettings(settings)),
    startWith(getLocalUserSettings()),
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnComplete: false,
        resetOnRefCountZero: false,
    }),
);

let updatedUserSetting$ = new Subject();

export let userSettingsUpdater$ = updatedUserSetting$.pipe(
    switchMap(([key, value]) => {
        let user = localStorage.getItem('user-settings');
        if (user) {
            user = JSON.parse(user);
        } else {
            user = {};
        }
        if (user[key] === value) {
            return EMPTY;
        }
        user[key] = value;
        saveUserSettings(user);
        return of([key, value]);
    }),
    bufferTime(300),
    mergeMap(updates => {
        if (updates.length === 0) {
            return EMPTY;
        }
        let setting = {};
        for (let [key, value] of updates) {
            setting[key] = value;
        }
        return postJson('/api/user-settings', setting);
    }, 1),
);

function saveUserSettings(settings) {
    localStorage.setItem('user-settings', JSON.stringify(settings));
}

export function updateUserSetting(key, value) {
    updatedUserSetting$.next([key, value]);
}

export function syncInputWithSettings(input, key) {
    let load$ = userSettings$.pipe(
        map(settings => {
            let val = settings[key];
            if (input.tagName === 'INPUT' && input.type === 'checkbox') {
                input.checked = val;
            } else {
                input.value = val;
            }
            input.value = val;
            return val;
        }),
    );
    let store$ = fromEvent(input, 'change').pipe(
        map(() => {
            let val;
            if (input.tagName === 'INPUT' && input.type === 'checkbox') {
                val = input.checked;
            } else {
                val = input.value;
            }
            updateUserSetting(key, val);
            return val;
        }),
    );
    return merge(load$, store$);
}

function getLocalUserSettings() {
    let user = localStorage.getItem('user-settings');
    if (user) {
        user = JSON.parse(user);
    } else {
        user = {};
    }
    return mergeUserSettings(defaultUserSettings, user);
}

function mergeUserSettings(a, b) {
    return {
        ...a,
        ...b,
    };
}
